import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type TouchEvent,
} from "react";
import type { MessageRow } from "../lib/db";
import {
  isChatFileAttachment,
  isChatImageAttachment,
  isChatVoiceAttachment,
  isTombstonePlaintext,
  parseMessageEnvelope,
  plaintextDisplayBody,
  previewTextForReply,
} from "../lib/crypto";
import { getStoredJwt } from "../lib/authApi";
import { firstHttpUrlFromText, isTextOnlyUrl } from "../lib/firstUrl";
import { LinkPreviewCard } from "./LinkPreviewCard";
import { MessageFileBody } from "./MessageFileBody";
import { MessageImageBody } from "./MessageImageBody";
import { MessageTextBody } from "./MessageTextBody";
import { MessageVoiceBody } from "./MessageVoiceBody";
import { AppIcon } from "./AppIcon";
import type { MessageReactionBadge } from "../app/state/domainTypes";
import { useTheme } from "../app/context/ThemeContext";
import { avatarTerminalTintStyle } from "../lib/avatarDisplay";
import { AvatarImgWithVariantFallback } from "./AvatarImgWithVariantFallback";

type MessageBubbleProps = {
  message: MessageRow;
  ownUserId: string;
  ownUsername: string;
  peerUsername: string;
  supportsReadReceipts: boolean;
  plaintext: string;
  /** Сообщение, на которое ссылаемся (undefined — нет reply; null — ссылка битая). */
  replyParent: MessageRow | null | undefined;
  isHighlighted: boolean;
  isMobile: boolean;
  reactions: MessageReactionBadge[];
  isMenuOpen: boolean;
  onToggleMenu: () => void;
  onCloseMenu: () => void;
  onReply: (message: MessageRow) => void;
  onForward?: (message: MessageRow) => void;
  onToggleReaction: (message: MessageRow, emoji: string, remove: boolean) => void;
  onScrollToMessage: (messageId: string) => void;
  canDeleteMessage?: boolean;
  onDeleteMessage?: (message: MessageRow) => void;
  /** Закрепление сообщения в этом чате (workspace / DM / vault). */
  isMessagePinned?: boolean;
  onTogglePinMessage?: (message: MessageRow) => void;
  /** DM и workspace topic: reply/реакции/меню (если capabilities включены). */
  supportsMessageActions?: boolean;
  /** Если задан, подписи чужих авторов (workspace) вместо одного peerUsername. */
  resolveRemoteAuthor?: (fromId: string) => string;
  resolveRemoteAuthorAvatarSources?: (fromId: string) => {
    primary: string | null;
    fallback: string | null;
  };
  resolveRemoteAuthorRole?: (fromId: string) => string | undefined;
  showAuthorName?: boolean;
  showAuthorAvatar?: boolean;
  reserveAuthorRail?: boolean;
  leadingAvatarPrimary?: string | null;
  leadingAvatarFallback?: string | null;
  leadingAvatarName?: string;
  /** Повторить отправку после ошибки очереди (outbox). */
  onRetryFailedOutbox?: (message: MessageRow) => void;
  /** Vault: скачивание ciphertext вложений с /api/vault-upload. */
  fetchE2eEncryptedBlob?: (blobId: string) => Promise<ArrayBuffer>;
};

export type { MessageReactionBadge };

const REACTION_OPTIONS = [
  { key: "thumbs-up", label: "Нравится" },
  { key: "heart", label: "Сердце" },
  { key: "laugh", label: "Смех" },
  { key: "annoyed", label: "Раздражение" },
  { key: "frown", label: "Грусть" },
  { key: "angry", label: "Злость" },
] as const;

function formatBubbleTimestamp(ts: number): string {
  const date = new Date(ts);
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  if (sameDay) {
    return date.toLocaleTimeString("ru-RU", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const dayDiff = Math.round((startOfToday - startOfDate) / 86400000);
  if (dayDiff === 1) {
    return "вчера";
  }

  return date.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
  });
}

/** Basename of `/public/emoji/*.svg` for outbound delivery status. */
function statusIconAssetName(message: MessageRow, supportsReadReceipts: boolean): string | null {
  if (message.failed) return "circle-x";
  if (message.pending && message.queued) return "clock";
  if (message.pending) return "circle-small";
  if (!supportsReadReceipts) return null;
  if (message.read) return "check-check";
  if (message.delivered) return "check";
  return "check";
}

function StatusIconGlyph({ name }: { name: string }) {
  const url = `/emoji/${name}.svg`;
  return (
    <span
      aria-hidden="true"
      className="bubble__status-icon"
      style={{
        maskImage: `url(${url})`,
        WebkitMaskImage: `url(${url})`,
      }}
    />
  );
}

function remoteDisplayName(
  fromId: string,
  ownId: string,
  ownName: string,
  peerName: string,
  resolveRemoteAuthor?: (fromId: string) => string,
): string {
  if (fromId === ownId) return ownName;
  if (resolveRemoteAuthor) return resolveRemoteAuthor(fromId);
  return peerName;
}

function authorAt(
  fromId: string,
  ownId: string,
  ownName: string,
  peerName: string,
  resolveRemoteAuthor?: (fromId: string) => string,
): string {
  return `@${remoteDisplayName(fromId, ownId, ownName, peerName, resolveRemoteAuthor)}`;
}

function authorInitial(name: string): string {
  const trimmed = name.trim();
  return trimmed ? trimmed[0]!.toUpperCase() : "?";
}

type AvatarSrcPair = { primary: string | null; fallback: string | null };

function pickRailAvatarSources(showAuthorAvatar: boolean, leading: AvatarSrcPair, author: AvatarSrcPair): AvatarSrcPair {
  if (!showAuthorAvatar) return { primary: null, fallback: null };
  const hasLeading = leading.primary != null || leading.fallback != null;
  if (hasLeading) return leading;
  return author;
}

const ACTION_MENU_GAP_PX = 8;
const ACTION_MENU_EDGE_PAD_PX = 8;

function scrollBodyRectForBubble(bubble: HTMLElement): DOMRect {
  const body = bubble.closest(".chat-shell__body");
  if (body) return body.getBoundingClientRect();
  return new DOMRect(0, 0, window.innerWidth, window.innerHeight);
}

export function MessageBubble({
  message,
  ownUserId,
  ownUsername,
  peerUsername,
  supportsReadReceipts,
  plaintext,
  replyParent,
  isHighlighted,
  isMobile,
  reactions,
  isMenuOpen,
  onToggleMenu,
  onCloseMenu,
  onReply,
  onForward,
  onToggleReaction,
  onScrollToMessage,
  canDeleteMessage = false,
  onDeleteMessage,
  isMessagePinned = false,
  onTogglePinMessage,
  supportsMessageActions = true,
  resolveRemoteAuthor,
  resolveRemoteAuthorAvatarSources,
  resolveRemoteAuthorRole,
  showAuthorName = false,
  showAuthorAvatar = false,
  reserveAuthorRail = false,
  leadingAvatarPrimary = null,
  leadingAvatarFallback = null,
  leadingAvatarName,
  onRetryFailedOutbox,
  fetchE2eEncryptedBlob,
}: MessageBubbleProps) {
  const { visualMode, preference } = useTheme();
  const mine = message.from_id === ownUserId;
  const isDeleted = isTombstonePlaintext(plaintext) || message.deleted === true;
  const messageActionsEnabled = supportsMessageActions && !isDeleted;
  const hasAuthorContext = !mine && typeof resolveRemoteAuthorAvatarSources === "function";
  const env = parseMessageEnvelope(plaintext);
  const imageAtt =
    env?.content_type === "image" && env.attachments?.[0] && isChatImageAttachment(env.attachments[0])
      ? env.attachments[0]
      : null;
  const fileAtt =
    env?.content_type === "file" && env.attachments?.[0] && isChatFileAttachment(env.attachments[0])
      ? env.attachments[0]
      : null;
  const voiceAtt =
    env?.content_type === "voice" && env.attachments?.[0] && isChatVoiceAttachment(env.attachments[0])
      ? env.attachments[0]
      : null;
  const caption =
    env?.content_type === "image" || env?.content_type === "file"
      ? (env.body ?? "")
      : env?.content_type === "voice"
        ? ""
        : plaintextDisplayBody(plaintext);
  const previewUrl = getStoredJwt() ? firstHttpUrlFromText(caption) : null;
  const isUrlOnly = Boolean(previewUrl) && isTextOnlyUrl(caption);
  const canCopyCaption = caption.trim().length > 0;
  const authorName = hasAuthorContext
    ? remoteDisplayName(message.from_id, ownUserId, ownUsername, peerUsername, resolveRemoteAuthor)
    : "";
  const authorAvatarSources: AvatarSrcPair =
    hasAuthorContext && resolveRemoteAuthorAvatarSources
      ? resolveRemoteAuthorAvatarSources(message.from_id)
      : { primary: null, fallback: null };
  const authorRole = hasAuthorContext ? (resolveRemoteAuthorRole?.(message.from_id) ?? null) : null;
  const railAvatarSources = pickRailAvatarSources(
    showAuthorAvatar,
    { primary: leadingAvatarPrimary ?? null, fallback: leadingAvatarFallback ?? null },
    authorAvatarSources,
  );
  const railAvatarName = leadingAvatarName ?? (authorName || (mine ? ownUsername : peerUsername));

  const [swiping, setSwiping] = useState(false);
  const [swipeX, setSwipeX] = useState(0);
  const touchStartX = useRef(0);
  const ignoreClickRef = useRef(false);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  const [actionMenuPlacement, setActionMenuPlacement] = useState<"above" | "below">("above");
  const [actionMenuTranslateX, setActionMenuTranslateX] = useState(0);
  const activeReactionKeys = new Set(reactions.filter((reaction) => reaction.ownSelected).map((reaction) => reaction.emoji));

  useLayoutEffect(() => {
    if (!messageActionsEnabled || !isMenuOpen) {
      setActionMenuPlacement("above");
      setActionMenuTranslateX(0);
      return;
    }

    const bubble = bubbleRef.current;
    const actions = actionsRef.current;
    if (!bubble || !actions) return;

    const bodyRect = scrollBodyRectForBubble(bubble);
    const bubbleRect = bubble.getBoundingClientRect();
    const menuHeight = actions.getBoundingClientRect().height;

    const spaceAbove = bubbleRect.top - bodyRect.top;
    const spaceBelow = bodyRect.bottom - bubbleRect.bottom;

    let nextPlacement: "above" | "below" = "above";
    if (menuHeight + ACTION_MENU_GAP_PX <= spaceAbove) {
      nextPlacement = "above";
    } else if (menuHeight + ACTION_MENU_GAP_PX <= spaceBelow) {
      nextPlacement = "below";
    } else {
      nextPlacement = spaceAbove >= spaceBelow ? "above" : "below";
    }

    if (nextPlacement !== actionMenuPlacement) {
      setActionMenuPlacement(nextPlacement);
      return;
    }

    const menuRect = actions.getBoundingClientRect();
    let tx = 0;
    if (menuRect.left < bodyRect.left + ACTION_MENU_EDGE_PAD_PX) {
      tx = bodyRect.left + ACTION_MENU_EDGE_PAD_PX - menuRect.left;
    } else if (menuRect.right > bodyRect.right - ACTION_MENU_EDGE_PAD_PX) {
      tx = bodyRect.right - ACTION_MENU_EDGE_PAD_PX - menuRect.right;
    }
    setActionMenuTranslateX(tx);
  }, [isMenuOpen, messageActionsEnabled, actionMenuPlacement]);

  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    if (!messageActionsEnabled) return;
    touchStartX.current = e.touches[0].clientX;
    setSwiping(false);
  };

  const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    if (!messageActionsEnabled || !isMobile) return;
    const diff = e.touches[0].clientX - touchStartX.current;
    const swipeDir = mine ? Math.min(0, diff) : Math.max(0, diff);
    const clamped = mine ? Math.max(-60, swipeDir) : Math.min(60, swipeDir);
    if (Math.abs(diff) > 10) setSwiping(true);
    setSwipeX(clamped);
  };

  const handleTouchEnd = (_e: TouchEvent<HTMLDivElement>) => {
    if (!messageActionsEnabled || !isMobile) return;
    if (Math.abs(swipeX) > 40) {
      onReply(message);
      onCloseMenu();
      ignoreClickRef.current = true;
    }
    setSwipeX(0);
    setSwiping(false);
  };

  const onBubbleClick = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      if (!messageActionsEnabled) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest("a, button")) return;
      if (ignoreClickRef.current) {
        ignoreClickRef.current = false;
        return;
      }
      onToggleMenu();
    },
    [onToggleMenu, messageActionsEnabled],
  );

  const showReplyStrip = replyParent !== undefined;

  const showOutboundStatus =
    mine &&
    (supportsReadReceipts ||
      message.pending === true ||
      message.queued === true ||
      message.failed === true);
  const statusAsset = showOutboundStatus ? statusIconAssetName(message, supportsReadReceipts) : null;

  const bubbleContent = (
    <div
      ref={bubbleRef}
      className={`bubble ${mine ? "bubble--me" : "bubble--them"} ${isHighlighted ? "bubble--flash" : ""} ${reserveAuthorRail || hasAuthorContext ? "bubble--author-context" : ""} ${isDeleted ? "bubble--deleted" : ""} ${mine && message.pending && message.queued ? "bubble--queued" : ""} ${mine && message.failed ? "bubble--failed" : ""}`}
      onClick={onBubbleClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        transform: swipeX !== 0 ? `translateX(${swipeX}px)` : undefined,
        transition: swiping ? undefined : "transform 0.2s ease",
        position: "relative",
        cursor: messageActionsEnabled ? "pointer" : "default",
      }}
    >
      {hasAuthorContext && showAuthorName ? (
        <div className="bubble__author-header">
          <span className="bubble__author-name">
            {authorAt(message.from_id, ownUserId, ownUsername, peerUsername, resolveRemoteAuthor)}
          </span>
          {authorRole ? (
            <span className="bubble__author-role">{authorRole}</span>
          ) : null}
        </div>
      ) : null}
      {showReplyStrip ? (
        <div className="bubble__reply-wrap">
          <button
            type="button"
            className={`bubble__reply ${replyParent ? "" : "bubble__reply--missing"}`}
            onClick={(e) => {
              e.stopPropagation();
              if (replyParent) onScrollToMessage(replyParent.id);
            }}
            disabled={!replyParent}
          >
            <span className="bubble__reply-author">
              {replyParent
                ? authorAt(
                    replyParent.from_id,
                    ownUserId,
                    ownUsername,
                    peerUsername,
                    resolveRemoteAuthor,
                  )
                : "…"}
            </span>
            <span className="bubble__reply-snippet">
              {replyParent ? previewTextForReply(replyParent.plaintext) : "Сообщение недоступно"}
            </span>
          </button>
        </div>
      ) : null}

      {imageAtt ? (
        <>
          {caption ? <MessageTextBody text={caption} className="bubble__text" /> : null}
          <div className="bubble__media">
            <MessageImageBody attachment={imageAtt} fetchE2eEncryptedBlob={fetchE2eEncryptedBlob} />
          </div>
        </>
      ) : fileAtt ? (
        <>
          {caption ? <MessageTextBody text={caption} className="bubble__text" /> : null}
          <div className="bubble__media">
            <MessageFileBody attachment={fileAtt} fetchE2eEncryptedBlob={fetchE2eEncryptedBlob} />
          </div>
        </>
      ) : voiceAtt ? (
        <div className="bubble__media">
          <MessageVoiceBody attachment={voiceAtt} fetchE2eEncryptedBlob={fetchE2eEncryptedBlob} />
        </div>
      ) : isUrlOnly ? null : (
        <MessageTextBody text={caption} className="bubble__text" />
      )}
      {previewUrl ? <LinkPreviewCard pageUrl={previewUrl} /> : null}
      <div className="bubble__meta">
        {!isDeleted && reactions.length > 0 ? (
          <div className="bubble__reactions" aria-label="Реакции">
            {reactions.map((reaction) => (
              <span
                key={reaction.emoji}
                className={`bubble__reaction-badge ${reaction.ownSelected ? "is-own" : ""}`}
              >
                <span
                  aria-hidden="true"
                  className="bubble__reaction-icon"
                  style={{
                    maskImage: `url(/emoji/${reaction.emoji}.svg)`,
                    WebkitMaskImage: `url(/emoji/${reaction.emoji}.svg)`,
                  }}
                />
                <span className="bubble__reaction-count">{reaction.count}</span>
              </span>
            ))}
          </div>
        ) : null}
        <div className="bubble__meta-end">
          {isMessagePinned ? (
            <span className="bubble__pin-badge" aria-label="Сообщение закреплено" title="Сообщение закреплено">
              <AppIcon name="pin" className="bubble__pin-icon" />
            </span>
          ) : null}
          <span className="bubble__time">{formatBubbleTimestamp(message.created_at)}</span>
          {showOutboundStatus && statusAsset ? (
            <span className="bubble__status">
              {message.failed && onRetryFailedOutbox ? (
                <button
                  type="button"
                  className="bubble__status-retry"
                  title="Не удалось отправить — нажмите, чтобы повторить"
                  aria-label="Повторить отправку"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRetryFailedOutbox(message);
                  }}
                >
                  <StatusIconGlyph name={statusAsset} />
                </button>
              ) : (
                <StatusIconGlyph name={statusAsset} />
              )}
            </span>
          ) : null}
        </div>
      </div>

      {messageActionsEnabled && isMenuOpen ? (
        <div
          ref={actionsRef}
          className={`bubble__actions bubble__actions--${actionMenuPlacement} ${mine ? "bubble__actions--me" : "bubble__actions--them"}`}
          style={{
            transform: actionMenuTranslateX !== 0 ? `translateX(${actionMenuTranslateX}px)` : undefined,
          }}
        >
          <div className="bubble__emoji-row">
            {REACTION_OPTIONS.map((reaction) => {
              const isActive = activeReactionKeys.has(reaction.key);
              return (
                <button
                  key={reaction.key}
                  type="button"
                  className={`bubble__emoji-btn ${isActive ? "is-active" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleReaction(message, reaction.key, isActive);
                    onCloseMenu();
                  }}
                  aria-label={reaction.label}
                  title={reaction.label}
                >
                  <span
                    aria-hidden="true"
                    className="bubble__emoji-icon"
                    style={{
                      maskImage: `url(/emoji/${reaction.key}.svg)`,
                      WebkitMaskImage: `url(/emoji/${reaction.key}.svg)`,
                    }}
                  />
                </button>
              );
            })}
          </div>
          <div className="bubble__action-row">
            <button
              type="button"
              className="bubble__action-btn"
              aria-label="Ответить"
              title="Ответить"
              onClick={(e) => {
                e.stopPropagation();
                onReply(message);
                onCloseMenu();
              }}
            >
              <span
                aria-hidden="true"
                className="bubble__action-icon"
                style={{
                  maskImage: "url(/emoji/reply.svg)",
                  WebkitMaskImage: "url(/emoji/reply.svg)",
                }}
              />
              <span className="bubble__action-label">reply</span>
            </button>
            {onTogglePinMessage && !message.pending ? (
              <button
                type="button"
                className="bubble__action-btn"
                aria-label={isMessagePinned ? "Открепить сообщение" : "Закрепить сообщение"}
                title={isMessagePinned ? "Открепить" : "Закрепить"}
                onClick={(e) => {
                  e.stopPropagation();
                  onTogglePinMessage(message);
                  onCloseMenu();
                }}
              >
                <span
                  aria-hidden="true"
                  className="bubble__action-icon"
                  style={{
                    maskImage: `url(/emoji/${isMessagePinned ? "pin-off" : "pin"}.svg)`,
                    WebkitMaskImage: `url(/emoji/${isMessagePinned ? "pin-off" : "pin"}.svg)`,
                  }}
                />
                <span className="bubble__action-label">pin</span>
              </button>
            ) : null}
            {canCopyCaption ? (
              <button
                type="button"
                className="bubble__action-btn"
                aria-label="Копировать"
                title="Копировать"
                onClick={(e) => {
                  e.stopPropagation();
                  void navigator.clipboard.writeText(caption).then(() => onCloseMenu());
                }}
              >
                <span
                  aria-hidden="true"
                  className="bubble__action-icon"
                  style={{
                    maskImage: "url(/emoji/files.svg)",
                    WebkitMaskImage: "url(/emoji/files.svg)",
                  }}
                />
                <span className="bubble__action-label">copy</span>
              </button>
            ) : null}
            {onForward ? (
              <button
                type="button"
                className="bubble__action-btn"
                aria-label="Переслать"
                title="Переслать"
                onClick={(e) => {
                  e.stopPropagation();
                  onForward(message);
                  onCloseMenu();
                }}
              >
                <span
                  aria-hidden="true"
                  className="bubble__action-icon"
                  style={{
                    maskImage: "url(/emoji/forward.svg)",
                    WebkitMaskImage: "url(/emoji/forward.svg)",
                  }}
                />
                <span className="bubble__action-label">forward</span>
              </button>
            ) : null}
            {canDeleteMessage && onDeleteMessage ? (
              <button
                type="button"
                className="bubble__action-btn bubble__action-btn--danger"
                aria-label="Удалить"
                title="Удалить"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteMessage(message);
                  onCloseMenu();
                }}
              >
                <span
                  aria-hidden="true"
                  className="bubble__action-icon"
                  style={{
                    maskImage: "url(/emoji/trash-2.svg)",
                    WebkitMaskImage: "url(/emoji/trash-2.svg)",
                  }}
                />
                <span className="bubble__action-label">delete</span>
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {messageActionsEnabled && isMobile && swipeX !== 0 ? (
        <span className={`bubble__swipe-hint ${mine ? "bubble__swipe-hint--me" : "bubble__swipe-hint--them"}`}>
          ↩ reply
        </span>
      ) : null}
    </div>
  );

  if (!reserveAuthorRail && !hasAuthorContext) return bubbleContent;

  return (
    <div className="bubble-author-row">
      <div className="bubble-author-row__rail" aria-hidden="true">
        {showAuthorAvatar ? (
          <div className="bubble-author-avatar">
            <AvatarImgWithVariantFallback
              primary={railAvatarSources.primary}
              fallback={railAvatarSources.fallback}
              imgClassName={`bubble-author-avatar__img${visualMode === "terminal" ? " bubble-author-avatar__img--terminal" : ""}`}
              style={visualMode === "terminal" ? avatarTerminalTintStyle(preference.palette.accent) : undefined}
              letterClassName="bubble-author-avatar__letter"
              initial={authorInitial(railAvatarName)}
            />
          </div>
        ) : null}
      </div>
      {bubbleContent}
    </div>
  );
}
